import { Box, Typography } from "@mui/material";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";

const HEIGHT = 40;

type Option<TValue extends string> = {
  readonly value: TValue;
  readonly label: string;
};

type Props<TValue extends string> = {
  /** Shared by the radio inputs, so it has to be unique on the page. */
  readonly name: string;
  readonly label?: string;
  readonly value: TValue;
  readonly options: Option<TValue>[];
  readonly onChange: (value: TValue) => void;
  readonly disabled?: boolean;
};

// Native radios underneath give the radio-group semantics and arrow-key navigation for free.
export const SegmentedControl = <TValue extends string>({ name, label, value, options, onChange, disabled = false }: Props<TValue>) => {
  const labelId = `${name}-label`;

  return (
    <Box>
      {label && (
        <Typography id={labelId} variant="body2" fontWeight={500} mb={Spacing.S}>
          {label}
        </Typography>
      )}

      <Box
        role="radiogroup"
        aria-labelledby={label ? labelId : undefined}
        display="grid"
        gridAutoFlow="column"
        gridAutoColumns="minmax(0, 1fr)"
        gap={Spacing.S}
        height={HEIGHT}
        p={Spacing.S}
        bgcolor={disabled ? "action.disabledBackground" : "common.white"}
        border={1}
        borderColor="divider"
        borderRadius={Radius.M}
      >
        {options.map((option) => {
          const isSelected = option.value === value;

          return (
            <Box
              key={option.value}
              component="label"
              position="relative"
              display="flex"
              alignItems="center"
              justifyContent="center"
              px={Spacing.M}
              borderRadius={Radius.S}
              sx={(theme) => ({
                cursor: disabled ? "default" : "pointer",
                bgcolor: isSelected ? "primary.light" : "transparent",
                boxShadow: isSelected ? `inset 0 0 0 1px ${theme.palette.primary.main}` : "none",
                "&:hover": { bgcolor: isSelected ? "primary.light" : disabled ? "transparent" : "grey.50" },
                "&:has(input:focus-visible)": { outline: `2px solid ${theme.palette.primary.main}` },
              })}
            >
              <Box
                component="input"
                type="radio"
                name={name}
                value={option.value}
                checked={isSelected}
                disabled={disabled}
                onChange={() => onChange(option.value)}
                position="absolute"
                sx={{ inset: 0, m: 0, opacity: 0, cursor: "inherit" }}
              />
              <Typography variant="body2" fontWeight={isSelected ? 500 : 400} color={disabled ? "text.disabled" : "text.primary"} noWrap>
                {option.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
